import React from 'react';
import { Button, Typography } from '@mui/material';
import ListIcon from '@mui/icons-material/List';
import AddIcon from '@mui/icons-material/Add';
import YooptaHtmlEditor from '../../../common/YooptaEditor';
import { Post } from '../Board.types';
import { ComponentSkinProps } from '../../../../types/component-skin';

/**
 * Board 컴포넌트의 기본 스킨
 * 게시판의 모든 UI 렌더링을 담당합니다.
 */
const BasicBoardSkin: React.FC<ComponentSkinProps> = (props) => {
  const { data, actions } = props;

  // 게시판 목록 페이지 렌더링
  const renderListMode = () => {
    return (
      <div className="board-list-mode">
        <div className="board-top-buttons">
          {data.boardTitle ? (
            <Typography variant="h4" component="h1" className="board-title">
              {data.boardTitle}
            </Typography>
          ) : (
            <div></div>
          )}
          
          {data.postsData?.permissions?.canWrite && data.showNewButton && (
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<AddIcon />}
              onClick={() => actions.navigateToNew(data.boardCode)}
              className="board-new-button"
            >
              {data.t('새 글쓰기')}
            </Button>
          )}
        </div>
        
        {data.postsLoading ? (
          <div className="board-loading">
            <div className="board-loading-spinner"></div>
            <div className="board-loading-text">{data.t('게시글을 불러오는 중...')}</div>
          </div>
        ) : (
          <>
            {data.posts && data.posts.length > 0 ? (
              <div className="board-list-table-container">
                <table className="board-list-table">
                  <thead className="board-list-thead">
                    <tr>
                      <th className="board-list-th board-list-th-id">{data.t('번호')}</th>
                      <th className="board-list-th board-list-th-title">{data.t('제목')}</th>
                      <th className="board-list-th board-list-th-author">{data.t('작성자')}</th>
                      <th className="board-list-th board-list-th-date">{data.t('작성일')}</th>
                      <th className="board-list-th board-list-th-views">{data.t('조회')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.posts.map((post: Post) => (
                      <tr 
                        key={post.id} 
                        onClick={() => actions.navigateToDetail(post.id)}
                        className="board-list-tr"
                      >
                        <td className="board-list-td board-list-td-id">{post.id}</td>
                        <td className="board-list-td board-list-td-title">
                          <div className="board-list-td-title-text">{post.title}</div>
                        </td>
                        <td className="board-list-td board-list-td-author">
                          {post.administratorId ? data.t('관리자') : 
                           (data.currentUser && post.userId && 
                            ((data.currentUser.id && data.currentUser.id.toString() === post.userId.toString()) || 
                             (data.currentUser.userId && data.currentUser.userId.toString() === post.userId.toString()))) ? data.t('나') : 
                           (post.user?.name || post.author || data.t('익명'))}
                        </td>
                        <td className="board-list-td board-list-td-date">{new Date(post.createdAt).toLocaleDateString()}</td>
                        <td className="board-list-td board-list-td-views">{post.views || post.viewCount || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="board-empty-list">
                {data.t('게시글이 없습니다.')}
              </div>
            )}

            {data.pagination && data.pagination.totalPages > 1 && (
              <div className="board-pagination">
                {Array.from({ length: data.pagination.totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    className={`board-pagination-button ${page === data.currentPage ? 'active' : ''}`}
                    onClick={() => actions.handlePageChange(page)}
                  >
                    {page}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // 게시글 상세 페이지 렌더링
  const renderDetailMode = () => {
    // 로딩 중 화면 표시
    if (data.postDetailLoading) {
      return (
        <div className="board-loading">
          <div className="board-loading-spinner"></div>
          <div className="board-loading-text">{data.t('게시글을 불러오는 중...')}</div>
        </div>
      );
    }

    // 게시글이 없는 경우 처리
    if (!data.postDetail) {
      return (
        <div className="board-empty-list">
          <Typography variant="h5" color="error">
            {data.t('게시글을 찾을 수 없습니다.')}
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<ListIcon />} 
            onClick={() => actions.navigateToList()}
            className="board-list-button"
            style={{ marginTop: '20px' }}
          >
            {data.t('목록으로 돌아가기')}
          </Button>
        </div>
      );
    }

    // API 응답 구조에 따라 게시글 데이터 추출
    const post = data.postDetail.post || data.postDetail;
    const comments = post.children || [];
    const permissions = post.permissions || {};
    
    return (
      <div className="board-detail-mode">
        <div className="board-detail-actions">
          <button 
            className="board-list-button"
            onClick={() => actions.navigateToList()}
          >
            <ListIcon style={{ fontSize: '1rem', marginRight: '4px' }} />
            {data.t('목록')}
          </button>
          
          {/* 권한에 따른 버튼 표시 */}
          {(permissions.canEdit || permissions.canDelete) && (
            <div className="board-action-buttons">
              {/* 수정하기 버튼 - API 권한과 설정값 모두 확인 */}
              {permissions.canEdit && data.showEditButton && (
                <button 
                  className="board-edit-button"
                  onClick={() => {
                    actions.setFormData({
                      title: post.title,
                      content: post.content,
                      originalBlock: post.originalBlock || null
                    });
                    actions.setBoardMode('edit');
                  }}
                >
                  {data.t('수정')}
                </button>
              )}
              
              {/* 삭제하기 버튼 - API 권한과 설정값 모두 확인 */}
              {permissions.canDelete && data.showDeleteButton && (
                <button 
                  className="board-delete-button"
                  onClick={() => actions.setShowDeleteModal(true)}
                >
                  {data.t('삭제')}
                </button>
              )}
            </div>
          )}
        </div>
        
        <div className="board-detail-container">
          <div className="board-detail-header">
            <h2 className="board-detail-title">{post.title}</h2>
            
            <div className="board-detail-info">
              <div className="board-detail-info-item">
                <span className="board-detail-info-label">{data.t('작성자')}:</span>
                {post.administratorId ? data.t('관리자') : 
                 (data.currentUser && post.userId && 
                  ((data.currentUser.id && data.currentUser.id.toString() === post.userId.toString()) || 
                   (data.currentUser.userId && data.currentUser.userId.toString() === post.userId.toString()))) ? data.t('나') : 
                 (post.user?.name || post.author || data.t('익명'))}
              </div>
              <div className="board-detail-info-item">
                <span className="board-detail-info-label">{data.t('작성일')}:</span>
                {new Date(post.createdAt).toLocaleDateString()}
              </div>
              <div className="board-detail-info-item">
                <span className="board-detail-info-label">{data.t('조회수')}:</span>
                {post.views || post.viewCount || 0}
              </div>
            </div>
          </div>
          
          <div className="board-detail-content">
            <div dangerouslySetInnerHTML={{ __html: post.content }} />
          </div>
        </div>
        
        <div className="board-comments-section">
          <h3 className="board-comments-title">
            {data.t('댓글')} ({post.totalComments || comments.length})
          </h3>
          
          {/* 디버그 정보 */}
          {console.log('[BasicBoardSkin] 댓글 디버그 - paginatedComments:', data.paginatedComments)}
          {console.log('[BasicBoardSkin] 댓글 디버그 - totalCommentPages:', data.totalCommentPages)}
          {console.log('[BasicBoardSkin] 댓글 디버그 - currentCommentPage:', data.currentCommentPage)}
          
          {data.paginatedComments && data.paginatedComments.length > 0 ? (
            <div className="board-comments-list">
              {data.paginatedComments.map((comment: any) => (
                <div key={comment.id} className="board-comment-item">
                  <div className="board-comment-header">
                    <span className="board-comment-author">
                      {comment.administratorId ? data.t('관리자') : 
                       (data.currentUser && comment.userId && 
                        ((data.currentUser.id && data.currentUser.id.toString() === comment.userId.toString()) || 
                         (data.currentUser.userId && data.currentUser.userId.toString() === comment.userId.toString()))) ? data.t('나') : 
                       (comment.user?.name || comment.author || data.t('익명'))}
                    </span>
                    <span className="board-comment-date">{new Date(comment.createdAt).toLocaleDateString()}</span>
                  </div>
                  {data.editingCommentId === comment.id ? (
                    <div className="board-comment-edit-form">
                      <textarea
                        className="board-comment-edit-textarea"
                        value={data.editingCommentContent}
                        onChange={(e) => actions.handleEditingCommentChange(e)}
                        placeholder={data.t('댓글을 입력하세요...')}
                        autoFocus
                      />
                      <div className="board-comment-edit-actions">
                        <button 
                          className="board-comment-save-button"
                          onClick={() => actions.handleUpdateComment()}
                        >
                          {data.t('저장')}
                        </button>
                        <button 
                          className="board-comment-cancel-button"
                          onClick={() => actions.handleCancelEditComment()}
                        >
                          {data.t('취소')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className="board-comment-content"
                      dangerouslySetInnerHTML={{ __html: comment.content }} 
                    />
                  )}
                  {/* 댓글 권한에 따른 수정/삭제 버튼 - 수정 중이 아닐 때만 표시 */}
                  {data.editingCommentId !== comment.id && (comment.permissions?.canEdit || comment.permissions?.canDelete) && (
                    <div className="board-comment-actions">
                      {comment.permissions?.canEdit && (
                        <button 
                          className="board-comment-edit-button"
                          onClick={() => actions.handleEditComment(comment.id, comment.content)}
                        >
                          {data.t('수정')}
                        </button>
                      )}
                      {comment.permissions?.canDelete && (
                        <button 
                          className="board-comment-delete-button"
                          onClick={() => actions.handleDeleteComment(comment.id)}
                        >
                          {data.t('삭제')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="board-no-comments">{data.t('아직 댓글이 없습니다.')}</div>
          )}
          
          {/* 댓글 페이지네이션 */}
          {data.totalCommentPages > 1 && (
            <div className="board-comment-pagination">
              {/* 이전 페이지 그룹 버튼 */}
              {data.commentPageRange && data.commentPageRange[0] > 1 && (
                <>
                  <button
                    className="board-comment-page-button"
                    onClick={() => actions.handleCommentPageChange(1)}
                    title={data.t('첫 페이지')}
                  >
                    «
                  </button>
                  <button
                    className="board-comment-page-button"
                    onClick={() => actions.handleCommentPageChange(Math.max(1, data.currentCommentPage - 5))}
                    title={data.t('이전 페이지 그룹')}
                  >
                    ‹
                  </button>
                </>
              )}
              
              {/* 페이지 번호 버튼 */}
              {data.commentPageRange && data.commentPageRange.map((page: number) => (
                <button
                  key={page}
                  className={`board-comment-page-button ${page === data.currentCommentPage ? 'active' : ''}`}
                  onClick={() => actions.handleCommentPageChange(page)}
                  disabled={page === data.currentCommentPage}
                >
                  {page}
                </button>
              ))}
              
              {/* 다음 페이지 그룹 버튼 */}
              {data.commentPageRange && data.commentPageRange[data.commentPageRange.length - 1] < data.totalCommentPages && (
                <>
                  <button
                    className="board-comment-page-button"
                    onClick={() => actions.handleCommentPageChange(Math.min(data.totalCommentPages, data.currentCommentPage + 5))}
                    title={data.t('다음 페이지 그룹')}
                  >
                    ›
                  </button>
                  <button
                    className="board-comment-page-button"
                    onClick={() => actions.handleCommentPageChange(data.totalCommentPages)}
                    title={data.t('마지막 페이지')}
                  >
                    »
                  </button>
                </>
              )}
            </div>
          )}
          
          {permissions.canComment && data.showCommentButton && (
            <div className="board-comment-form">
              <div className="board-comment-form-inner">
                <textarea
                  placeholder={data.t('댓글을 입력하세요...')}
                  value={data.commentData.content}
                  onChange={actions.handleCommentChange}
                  className="board-comment-textarea"
                />
                <div className="board-comment-submit">
                  <button 
                    className="board-comment-button"
                    onClick={actions.handleCommentSubmit}
                  >
                    {data.t('댓글 작성')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {data.showDeleteModal && (
          <div className="board-delete-modal-overlay">
            <div className="board-delete-modal">
              <h3 className="board-delete-modal-title">{data.t('게시글을 삭제하시겠습니까?')}</h3>
              <p className="board-delete-modal-text">{data.t('삭제한 게시글은 복구할 수 없습니다.')}</p>
              <div className="board-delete-modal-actions">
                <button 
                  className="board-delete-modal-cancel"
                  onClick={() => actions.cancelDeletePost()}
                >
                  {data.t('취소')}
                </button>
                <button 
                  className="board-delete-modal-confirm"
                  onClick={() => actions.confirmDeletePost()}
                >
                  {data.t('삭제')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // 게시글 작성 페이지 렌더링
  const renderNewMode = () => {
    return (
      <div className="board-form-mode">
        <div className="board-form-container">
          <div className="board-form-header">
            <h2 className="board-form-title">
              {data.boardTitle ? `${data.boardTitle} - ${data.t('새 글 작성')}` : data.t('새 글 작성')}
            </h2>
          </div>
          
          <div className="board-form-content">
            <div className="board-form-group">
              <label htmlFor="title" className="board-form-label">{data.t('제목')}</label>
              <input
                type="text"
                id="title"
                name="title"
                value={data.formData.title}
                onChange={actions.handleInputChange}
                className="board-form-input"
                placeholder={data.t('제목을 입력하세요')}
              />
            </div>
            
            <div className="board-form-group">
              <label htmlFor="content" className="board-form-label">{data.t('내용')}</label>
              <div className="board-form-editor">
                <YooptaHtmlEditor
                  initialData=""
                  onChange={actions.handleEditorChange}
                  placeholder={data.t('내용을 입력하세요')}
                  editorKey={`board-new-${data.categoryId || data.selectedBoardId || 'default'}`}
                  key={`board-new-editor`}
                />
              </div>
            </div>
            
            <div className="board-form-actions">
              <button 
                className="board-form-cancel"
                onClick={actions.handleCancelNew}
              >
                {data.t('취소')}
              </button>
              <button 
                className="board-form-submit"
                onClick={actions.handleSavePost}
              >
                {data.t('저장')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 게시글 수정 페이지 렌더링
  const renderEditMode = () => {
    return (
      <div className="board-form-mode">
        <div className="board-form-container">
          <div className="board-form-header">
            <h2 className="board-form-title">
              {data.boardTitle ? `${data.boardTitle} - ${data.t('게시글 수정')}` : data.t('게시글 수정')}
            </h2>
          </div>
          
          <div className="board-form-content">
            <div className="board-form-group">
              <label htmlFor="title" className="board-form-label">{data.t('제목')}</label>
              <input
                type="text"
                id="title"
                name="title"
                value={data.formData.title}
                onChange={actions.handleInputChange}
                className="board-form-input"
                placeholder={data.t('제목을 입력하세요')}
              />
            </div>
            
            <div className="board-form-group">
              <label htmlFor="content" className="board-form-label">{data.t('내용')}</label>
              <div className="board-form-editor">
                <YooptaHtmlEditor
                  initialData={data.editorInitialData}
                  onChange={actions.handleEditorChange}
                  placeholder={data.t('내용을 입력하세요')}
                  editorKey={`board-edit-${data.postId || data.categoryId || data.selectedBoardId || 'default'}`}
                  key={`board-edit-${data.postId || 'edit'}`}
                />
              </div>
            </div>
            
            <div className="board-form-actions">
              <button 
                className="board-form-cancel"
                onClick={actions.handleCancelEdit}
              >
                {data.t('취소')}
              </button>
              <button 
                className="board-form-submit"
                onClick={actions.handleSavePost}
              >
                {data.t('저장')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 상황에 맞는 컴포넌트 렌더링
  const renderContent = () => {
    // 모드에 따라 다른 컴포넌트 렌더링
    switch (data.mode) {
      case 'detail':
        return renderDetailMode();
      case 'new':
        return renderNewMode();
      case 'edit':
        return renderEditMode();
      case 'list':
      default:
        return renderListMode();
    }
  };

  // 최종 렌더링
  return (
    <section 
      className={`board-component-container ${data.isFinappMode ? 'finapp-mode' : ''}`}
      data-device={data.currentDevice}
      data-finapp={data.isFinappMode ? 'true' : 'false'}
      style={data.style}
    >
      {renderContent()}
    </section>
  );
};

export default BasicBoardSkin;